import { useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import add from 'date-fns/add'
import startOfDay from 'date-fns/startOfDay'
import { useEffect, useMemo, useState, useCallback } from 'react'
import { useParams } from 'react-router-dom'

import { BackContinue } from '@components/back_continue/back_continue'
import { useBookingContext } from '@providers/booking'
import { useRoutes } from 'app/use_routes'
import { MAX_WIDGET_SIZE, STICKY_FOOTER_HEIGHT } from '@shared/constants'
import { buildSearchParamsForTemperTest } from '@shared/header_navigation'
import { i18WithParams as t } from '@shared/locale'
import { Spacer, Spacer2, formatListWithConjunction } from 'components-ui'
import { useRedirectNoData } from 'hooks/use_redirect_no_data'
import { Hooks, Types } from 'service-api'
import { DatePicker } from './date_picker'
import { DatePickerSkeleton } from './date_picker_skeleton'
import { DateViewer } from './date_viewer'
import type { TDaysData } from './temper_test.d'

export const TemperTest = () => {
  const { accountName = '' } = useParams()
  const bookingCtx = useBookingContext()
  const { navigateRoute } = useRoutes()
  const [tempTestData, setTempTestData] = useState<Types.BookingSearchByDaysClientDto>()
  const [daysData, setDaysData] = useState<TDaysData>()
  const theme = useTheme()
  const isBelowMD = useMediaQuery(theme.breakpoints.down('md'))

  // redirect to home if the state has invalid data
  useRedirectNoData()

  useEffect(() => {
    bookingCtx.dispatch({ type: 'SET_SEARCH_STATE', payload: { value: 'temperTest' } })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingCtx.state.currentState])

  const today = useMemo(() => new Date(), [])
  const tempTestRange = add(today, {
    minutes:
      bookingCtx.state.config.booking?.maxBookingWindow?.[0]?.min ||
      bookingCtx.state.config.booking?.defaultMaxBookingWindow ||
      0,
  })

  const searchParams = useMemo(
    () => buildSearchParamsForTemperTest(bookingCtx, today, tempTestRange),
    [bookingCtx, today, tempTestRange]
  )
  const temperTest = Hooks.useClientBookingSearchByDays(accountName, 'temper-test', searchParams)
  const startDate = useMemo(() => new Date(`${searchParams.startDate}T00:00:00.000`), [searchParams.startDate])

  useEffect(() => {
    document.title = t('title.scheduleEvaluation')
  }, [])

  useEffect(() => {
    if (temperTest.isSuccess && !tempTestData) {
      const results: Types.BookingSearchByDaysClientDto[] = temperTest.data!.results
      setTempTestData(results[0])
      bookingCtx.dispatch({ type: 'SET_TEMPER_TEST_OFFER', payload: { value: results?.[0] } })

      const data = results?.[0]?.availabilityGroupsByDays?.reduce((acc, dd, idx) => {
        const baseDate = startOfDay(add(startDate, { days: idx }))
        acc[baseDate.valueOf()] = { ...dd[0] } as Types.AvailabilityGroupClientDto
        return acc
      }, {} as TDaysData)

      setDaysData(data)
    }
  }, [tempTestData, temperTest.data, temperTest.data?.results, temperTest.isSuccess, startDate, bookingCtx])

  const temperTestName = useMemo(
    () => bookingCtx.state.data.temperTest?.displayName || t('temperTest.defaultName'),
    [bookingCtx.state.data.temperTest?.displayName]
  )
  const petNames = useMemo(
    () => bookingCtx.state.cart.groups.flatMap((group) => group.pets.map((pet) => pet.name)),
    [bookingCtx.state.cart.groups]
  )

  const pageTitle = t('temperTest.title', { temperTestName })
  const pageSubtitle = t('temperTest.subtitle', { temperTestName, petNames: formatListWithConjunction(petNames) })

  const canContinue = useMemo(() => {
    return (
      typeof bookingCtx.state.cart.temperTest?.date !== 'undefined' &&
      typeof bookingCtx.state.cart.temperTest?.time !== 'undefined'
    )
  }, [bookingCtx.state.cart.temperTest?.date, bookingCtx.state.cart.temperTest?.time])

  const onBackHandler = useCallback(
    (ev?: React.SyntheticEvent) => {
      ev?.preventDefault()
      navigateRoute('searchServiceType', { serviceTypeName: 'boarding' })
    },
    [navigateRoute, bookingCtx.state.serviceTypeName]
  )

  if (!bookingCtx.state.startDate) {
    return null
  }

  return (
    <Box sx={{ maxWidth: MAX_WIDGET_SIZE, margin: '0 auto' }}>
      <Spacer height={3} />
      <Typography variant="h1" sx={{ px: 2 }}>
        {pageTitle}
      </Typography>
      <Spacer2 />

      <Paper elevation={1} sx={{ p: 2, borderRadius: `${isBelowMD ? 0 : theme.shape.borderRadius}px` }}>
        <Typography variant="h5">{pageSubtitle}</Typography>

        <Spacer2 />

        <Stack direction={{ sx: 'column', md: 'row' }} alignItems="start" spacing={3}>
          {temperTest.isLoading ? (
            <DatePickerSkeleton />
          ) : (
            <DatePicker daysData={daysData} startDate={startOfDay(today)} maxDate={tempTestRange} />
          )}
          <DateViewer />
        </Stack>
      </Paper>

      <Spacer height={STICKY_FOOTER_HEIGHT} />
      <BackContinue continueEnabled={canContinue} onBack={onBackHandler} />
    </Box>
  )
}
